<script setup lang="ts">
import {
  useToggleWishlistMutation,
  useWishlistQuery,
} from "~~/app/composables/queries/useStoreApi";
import { useAnalytics } from "~~/app/composables/useAnalytics";
import type { Product } from "~~/shared/types/store";

const props = withDefaults(
  defineProps<{ product: Product; alwaysVisible?: boolean }>(),
  {
    alwaysVisible: false,
  },
);

const toggleWishlist = useToggleWishlistMutation();
const { data: wishlist } = useWishlistQuery();
const analytics = useAnalytics();
// Ulubione wymagają konta, a sklep katalogowy kont nie ma (/favorites jest tam
// zablokowane) — serduszko tylko udawało, że coś zapisuje.
const { sellingEnabled } = useCatalogOnly();

const isFavorite = computed(
  () => wishlist.value?.productIds?.includes(props.product.id) || false,
);

function toggleProductFavorite() {
  const wasFavorite = isFavorite.value;
  toggleWishlist.mutate(
    { productId: props.product.id, isFavorite: wasFavorite },
    {
      onSuccess: () => {
        if (wasFavorite) analytics.trackRemoveFromWishlist(props.product);
        else analytics.trackAddToWishlist(props.product);
      },
    },
  );
}
</script>

<template>
  <div
    v-if="sellingEnabled"
    :class="[
      'product-card-actions',
      { 'product-card-actions--always-visible': alwaysVisible },
    ]"
  >
    <button
      class="product-card-actions__button"
      type="button"
      :aria-label="isFavorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'"
      :class="{ 'is-favorite': isFavorite }"
      @click.stop.prevent="toggleProductFavorite"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        :fill="isFavorite ? 'currentColor' : 'none'"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path
          d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
        />
      </svg>
    </button>
  </div>
</template>

<style scoped>
.product-card-actions {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
}

.product-card-actions--always-visible {
  opacity: 1;
}

.product-card-actions__button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 0;
  cursor: pointer;
  color: var(--color-black);
  background: transparent;
}

.product-card-actions__button:hover svg {
  fill: var(--color-black);
}

.product-card-actions__button.is-favorite {
  color: var(--color-sale);
}
</style>
